"use client";

import { c as _c } from "react/compiler-runtime";
import { Section } from "@/components/section";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import { SVGProps } from "react";
import { motion } from "motion/react";
import { Card } from "@/components/ui/card";
import { GraduationCap, Laptop, Users, UserCheck, Handshake } from "lucide-react";
type WhatSpoclearnOffersYouProps = {
  section: PageSections;
  data: {
    heading: string;
    seoHeading: string;
    description: string;
    buttonText: string;
    buttonAction: string;
    cards: {
      title: string;
      description: string;
      color: string;
    }[];
    icon?: ({
      title,
      titleId,
      desc,
      descId,
      ...props
    }: SVGProps<SVGSVGElement> & {
      title: string;
      titleId: string;
      desc: string;
      descId: string;
    }) => React.JSX.Element;
  };
};
const getCardIcon = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("consulting")) return Handshake;
  if (t.includes("classroom") || t.includes("person") || t.includes("physical")) return GraduationCap;
  if (t.includes("online") || t.includes("virtual") || t.includes("live")) return Laptop;
  if (t.includes("corporate") || t.includes("group") || t.includes("team")) return Users;
  if (t.includes("1-to-1") || t.includes("one-to-one") || t.includes("private") || t.includes("personal")) return UserCheck;
  return GraduationCap;
};
const getCardBadge = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("consulting")) return "Strategic Advisory";
  if (t.includes("classroom") || t.includes("person") || t.includes("physical")) return "In-Person Classroom";
  if (t.includes("online") || t.includes("virtual") || t.includes("live")) return "Interactive Live";
  if (t.includes("corporate") || t.includes("group") || t.includes("team")) return "Custom For Teams";
  if (t.includes("1-to-1") || t.includes("one-to-one") || t.includes("private") || t.includes("personal")) return "1-on-1 Mentorship";
  return "Flexible Option";
};
const getBadgeStyles = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("consulting")) {
    return "bg-blue-50 text-blue-700 border-blue-200/60 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800/80";
  }
  if (t.includes("classroom") || t.includes("person") || t.includes("physical")) {
    return "bg-purple-50 text-purple-700 border-purple-200/60 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-800/80";
  }
  if (t.includes("online") || t.includes("virtual") || t.includes("live")) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200/60 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800/80";
  }
  if (t.includes("corporate") || t.includes("group") || t.includes("team")) {
    return "bg-amber-50 text-amber-700 border-amber-200/60 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800/80";
  }
  if (t.includes("1-to-1") || t.includes("one-to-one") || t.includes("private") || t.includes("personal")) {
    return "bg-rose-50 text-rose-700 border-rose-200/60 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800/80";
  }
  return "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-900/50 dark:text-zinc-300 dark:border-zinc-800";
};
const getIconStyles = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes("consulting")) {
    return "bg-blue-500/10 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400";
  }
  if (t.includes("classroom") || t.includes("person") || t.includes("physical")) {
    return "bg-purple-500/10 text-purple-600 dark:bg-purple-950/40 dark:text-purple-400";
  }
  if (t.includes("online") || t.includes("virtual") || t.includes("live")) {
    return "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400";
  }
  if (t.includes("corporate") || t.includes("group") || t.includes("team")) {
    return "bg-amber-500/10 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400";
  }
  if (t.includes("1-to-1") || t.includes("one-to-one") || t.includes("private") || t.includes("personal")) {
    return "bg-rose-500/10 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400";
  }
  return "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground";
};
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
} as const;
const itemVariants = {
  hidden: {
    y: 20,
    opacity: 0
  },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 90,
      damping: 16
    }
  }
} as const;
const WhatSpoclearnOffersYou = t0 => {
  const $ = _c(16);
  const {
    data,
    section
  } = t0;
  const t1 = section.sectionId;
  let t2;
  if ($[0] !== section.sectionClass) {
    t2 = cn(section.sectionClass, "bg-primary/5 dark:bg-background");
    $[0] = section.sectionClass;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = data.heading;
  const t4 = data.seoHeading;
  const t5 = data.description;
  const t6 = data.buttonAction;
  const t7 = data.buttonText;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      once: true,
      margin: "-50px"
    };
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  let t9;
  if ($[3] !== data.cards) {
    t9 = data.cards.map(_temp);
    $[3] = data.cards;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== t9) {
    t10 = <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={t8} className="grid grid-cols-1 gap-6 md:grid-cols-3">{t9}</motion.div>;
    $[5] = t9;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== data.buttonAction || $[8] !== data.buttonText || $[9] !== data.description || $[10] !== data.heading || $[11] !== data.seoHeading || $[12] !== section.sectionId || $[13] !== t10 || $[14] !== t2) {
    t11 = <Section id={t1} className={t2} title={t3} seoTitle={t4} paragraph={t5} path={t6} actionLabel={t7}>{t10}</Section>;
    $[7] = data.buttonAction;
    $[8] = data.buttonText;
    $[9] = data.description;
    $[10] = data.heading;
    $[11] = data.seoHeading;
    $[12] = section.sectionId;
    $[13] = t10;
    $[14] = t2;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
};
export default WhatSpoclearnOffersYou;
function _temp(card, index) {
  const IconComponent = getCardIcon(card.title);
  const badgeText = getCardBadge(card.title);
  return <motion.div key={`what-spoclearn-offer-card-${index}`} variants={itemVariants} className={cn("h-full", {
    "md:col-span-2": index === 0
  })}><Card className="group/card bg-card hover:ring-primary/20 flex h-full flex-col justify-between p-6 shadow-xs transition-all duration-300 hover:-translate-y-1 hover:shadow-md"><div className="space-y-5"><div className="flex items-center justify-between"><div className={cn("flex size-10 items-center justify-center rounded-xl transition-transform duration-300 group-hover/card:scale-105", getIconStyles(card.title), {
            "size-12": index === 0
          })}><IconComponent className={cn("size-5", {
              "size-6": index === 0
            })} /></div><span className={cn("rounded-full border px-2.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase transition-colors duration-200", getBadgeStyles(card.title), {
            "px-3 py-1": index === 0
          })}>{badgeText}</span></div><div className="space-y-2"><Text as="div" weight="bold" className={cn("text-foreground text-lg font-bold tracking-tight", {
            "text-2xl": index === 0
          })}>{card.title}</Text><Text className={cn("text-muted-foreground text-sm leading-relaxed", {
            "max-w-2xl text-base": index === 0
          })}>{card.description}</Text></div></div></Card></motion.div>;
}